import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, Download, IndianRupee, Trash2, Filter, Search, AlertTriangle, Clock, ZapOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function AdminReferrals() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [commissionAmount, setCommissionAmount] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states
  const [timePeriod, setTimePeriod] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [minOrders, setMinOrders] = useState('');
  const [minRevenue, setMinRevenue] = useState('');
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchReferrals();
    fetchPartners();
  }, [timePeriod, statusFilter, activityFilter, partnerFilter, minOrders, minRevenue]);

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API}/admin/partners`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPartners(data);
      }
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  };

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/referrals?`;
      if (timePeriod && timePeriod !== 'all') url += `time_period=${timePeriod}&`;
      if (statusFilter && statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (activityFilter && activityFilter !== 'all') url += `activity_status=${activityFilter}&`;
      if (partnerFilter && partnerFilter !== 'all') url += `partner_id=${partnerFilter}&`;
      if (minOrders) url += `min_orders=${minOrders}&`;
      if (minRevenue) url += `min_revenue=${minRevenue}&`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) throw new Error('Failed to fetch referrals');
      
      const data = await response.json();
      setReferrals(data);
    } catch (error) {
      toast.error('Failed to load referrals');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (referral) => {
    setReferralToDelete(referral);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!referralToDelete) return;
    
    try {
      const response = await fetch(`${API}/admin/referrals/${referralToDelete.referral_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete referral');
      }
      
      toast.success(`Referral for "${referralToDelete.customer_name}" deleted successfully`);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to delete referral');
    } finally {
      setDeleteDialogOpen(false);
      setReferralToDelete(null);
    }
  };

  // Filter referrals by search query (client-side)
  const filteredReferrals = referrals.filter(referral => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (referral.customer_name || '').toLowerCase().includes(query) ||
      (referral.customer_email || '').toLowerCase().includes(query) ||
      (referral.customer_phone || '').toLowerCase().includes(query) ||
      (referral.partner_name || '').toLowerCase().includes(query)
    );
  });

  const updateReferralStatus = async (referralId, status, commission = null) => {
    try {
      const body = { status };
      if (commission !== null) {
        body.commission_amount = commission;
      }
      
      const response = await fetch(`${API}/admin/referrals/${referralId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update referral');
      }
      
      toast.success(`Referral marked as ${status}`);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to update referral status');
    }
  };

  const handleStatusChange = (referral, newStatus) => {
    if (newStatus === 'paid' && referral.status === 'pending') {
      // Show commission dialog when marking as paid
      setSelectedReferral(referral);
      setCommissionAmount('');
      setShowCommissionDialog(true);
    } else {
      updateReferralStatus(referral.referral_id, newStatus);
    }
  };

  const handleConfirmPaid = () => {
    if (!selectedReferral) return;
    
    const commission = parseFloat(commissionAmount);
    if (isNaN(commission) || commission < 0) {
      toast.error('Please enter a valid commission amount');
      return;
    }
    
    updateReferralStatus(selectedReferral.referral_id, 'paid', commission);
    setShowCommissionDialog(false);
    setSelectedReferral(null);
    setCommissionAmount('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'active':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'expired':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'pending':
      default:
        return 'bg-orange-100 text-orange-700 border-orange-200';
    }
  };

  const handleRecalculateCommission = async (referralId) => {
    try {
      const response = await fetch(`${API}/admin/referrals/${referralId}/recalculate`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to recalculate');
      }
      
      const result = await response.json();
      toast.success(result.message);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to recalculate commission');
    }
  };

  const handleRecalculateAll = async () => {
    if (!confirm('This will recalculate commissions for ALL referrals from their linked transactions. Continue?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/referrals/recalculate-all`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to recalculate');
      }
      
      const result = await response.json();
      toast.success(result.message);
      fetchReferrals();
    } catch (error) {
      toast.error(error.message || 'Failed to recalculate commissions');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stats = {
    total: referrals.length,
    active: referrals.filter(r => r.status === 'active' || r.status === 'pending').length,
    expired: referrals.filter(r => r.status === 'expired').length,
    ordering: referrals.filter(r => (r.total_orders > 0 || r.transaction_count > 0)).length,
    noOrders: referrals.filter(r => (!r.total_orders && !r.transaction_count)).length,
    totalRevenue: referrals.reduce((sum, r) => sum + (r.total_revenue || 0), 0)
  };

  const getActivityBadge = (activityStatus) => {
    switch (activityStatus) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Ordering</Badge>;
      case 'dormant':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200"><Clock className="h-3 w-3 mr-1" />No Recent Orders</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-600 border-gray-200"><ZapOff className="h-3 w-3 mr-1" />No Orders Yet</Badge>;
      default:
        return null;
    }
  };

  const clearFilters = () => {
    setTimePeriod('all');
    setStatusFilter('all');
    setActivityFilter('all');
    setPartnerFilter('all');
    setMinOrders('');
    setMinRevenue('');
    setSearchQuery('');
  };

  const hasActiveFilters = timePeriod !== 'all' || statusFilter !== 'all' || activityFilter !== 'all' || partnerFilter !== 'all' || minOrders || minRevenue || searchQuery;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Referrals
          </h1>
          <p className="text-muted-foreground">Manage all partner referrals and update their status</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleRecalculateAll}
            variant="outline"
            className="text-blue-600 hover:bg-blue-50"
            data-testid="recalculate-all-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Recalculate All Commissions
          </Button>
          <Button 
            onClick={async () => {
              try {
                const response = await fetch(`${API}/admin/referrals/export`, {
                  headers: getAuthHeaders(),
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `referrals_export_${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Referrals exported successfully');
                } else {
                  toast.error('Failed to export referrals');
                }
              } catch (error) {
                toast.error('Failed to export referrals');
              }
            }}
            variant="outline"
            data-testid="export-all-referrals-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={fetchReferrals} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.total}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.active}
            </div>
            <p className="text-xs text-muted-foreground">Within 12-month window</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.expired}
            </div>
            <p className="text-xs text-muted-foreground">Past 12 months</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ordering</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.ordering}
            </div>
            <p className="text-xs text-muted-foreground">Has placed orders</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">No Orders Yet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {stats.noOrders}
            </div>
            <p className="text-xs text-muted-foreground">Never ordered</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ₹{stats.totalRevenue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by customer, email, phone, partner..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-referrals-input"
                />
              </div>
            </div>
            
            <div className="w-40">
              <Label className="text-xs mb-1 block">Time Period</Label>
              <Select value={timePeriod} onValueChange={setTimePeriod}>
                <SelectTrigger data-testid="time-period-filter">
                  <SelectValue placeholder="Time Period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 90 Days</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-36">
              <Label className="text-xs mb-1 block">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active (Earning)</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-48">
              <Label className="text-xs mb-1 block">Partner</Label>
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger data-testid="partner-filter">
                  <SelectValue placeholder="Filter by Partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {partners.map(partner => (
                    <SelectItem key={partner.user_id} value={partner.user_id}>
                      {partner.name || partner.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Label className="text-xs mb-1 block">Activity</Label>
              <Select value={activityFilter} onValueChange={setActivityFilter}>
                <SelectTrigger data-testid="activity-filter">
                  <SelectValue placeholder="Activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activity</SelectItem>
                  <SelectItem value="active">Ordering (Has purchases)</SelectItem>
                  <SelectItem value="dormant">No Recent Orders (2+ months)</SelectItem>
                  <SelectItem value="inactive">No Orders Yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-32">
              <Label className="text-xs mb-1 block">Min Orders</Label>
              <Input
                type="number"
                placeholder="Min orders"
                value={minOrders}
                onChange={(e) => setMinOrders(e.target.value)}
                min="0"
                data-testid="min-orders-filter"
              />
            </div>
            
            <div className="w-36">
              <Label className="text-xs mb-1 block">Min Revenue (₹)</Label>
              <Input
                type="number"
                placeholder="Min revenue"
                value={minRevenue}
                onChange={(e) => setMinRevenue(e.target.value)}
                min="0"
                data-testid="min-revenue-filter"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-muted-foreground mt-3">
              Showing {filteredReferrals.length} of {referrals.length} referrals
            </p>
          )}
        </CardContent>
      </Card>

      {/* Referrals Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Referrals</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredReferrals.length === 0 ? (
            <div className="text-center py-8">
              <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {hasActiveFilters ? 'No referrals match your filters' : 'No referrals yet'}
              </p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReferrals.map((referral) => (
                <div
                  key={referral.referral_id}
                  data-testid={`referral-${referral.referral_id}`}
                  className={`flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors ${
                    (referral.total_orders > 0 || referral.transaction_count > 0) ? 'border-green-300 bg-green-50/50' : 
                    referral.status === 'expired' ? 'border-gray-200 bg-gray-50/30' :
                    'border-blue-200 bg-blue-50/30'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{referral.customer_name}</p>
                          {referral.auto_created && (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                              Auto-created
                            </Badge>
                          )}
                          {getActivityBadge(referral.activity_status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Partner: {referral.partner_name}
                          {referral.auto_created_from_order && (
                            <span className="ml-2 text-xs text-purple-600">
                              (from order {referral.auto_created_from_order})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Contact:</span>
                        <span className="ml-2">{referral.customer_email || referral.customer_phone || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Orders:</span>
                        <span className="ml-2 font-semibold">{referral.transaction_count || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Revenue:</span>
                        <span className="ml-2 font-semibold text-blue-600">
                          ₹{(referral.total_revenue || 0).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Commission:</span>
                        <span className="ml-2 font-semibold text-green-600">
                          ₹{(referral.total_commission_earned || referral.commission_amount || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>Created: {new Date(referral.created_at).toLocaleDateString()}</span>
                      {referral.last_transaction_date && (
                        <span>Last Order: {new Date(referral.last_transaction_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRecalculateCommission(referral.referral_id)}
                      title="Recalculate commission from transactions"
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Badge 
                      className={`${
                        referral.status === 'expired' 
                          ? 'bg-gray-100 text-gray-700 border-gray-200' 
                          : 'bg-blue-100 text-blue-700 border-blue-200'
                      }`}
                    >
                      {referral.status === 'expired' ? 'Expired' : 'Active'}
                    </Badge>
                    {referral.status !== 'expired' && !referral.total_orders && !referral.transaction_count && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(referral)}
                        title="Delete referral"
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`delete-referral-${referral.referral_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Referral
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the referral for &quot;{referralToDelete?.customer_name}&quot;?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Commission Dialog */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Referral as Paid</DialogTitle>
            <DialogDescription>
              Enter the commission amount to credit to the partner for this referral.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedReferral && (
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p><strong>Customer:</strong> {selectedReferral.customer_name}</p>
                <p><strong>Partner:</strong> {selectedReferral.partner_name}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="commission">Commission Amount (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="commission"
                  type="number"
                  placeholder="Enter commission amount"
                  value={commissionAmount}
                  onChange={(e) => setCommissionAmount(e.target.value)}
                  className="pl-10"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmPaid} className="bg-green-600 hover:bg-green-700">
              Confirm & Credit Commission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
